import React, { useEffect, useState } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import api from '../services/api';
import { fetchAdminOrders } from '../services/orders';
import './Analytics.css';

const statusLabels = {
  pending: 'Pending',
  processing: 'Processing',
  purchased: 'Purchased',
  shipped_out: 'Shipped Out',
  in_nigeria: 'In Nigeria',
  completed: 'Completed',
  refund: 'Refund',
  cancelled: 'Cancelled',
};

const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        // Get overall stats + up to 100 most recent orders for analytics
        const [statsRes, ordersRes] = await Promise.all([
          api.get('/admin/dashboard/stats'),
          fetchAdminOrders({ page: 1, per_page: 100 }),
        ]);
        setStats(statsRes.data || {});
        setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      } catch (err) {
        console.error('Failed to load analytics', err);
        setError(err.response?.data?.message || err.message || 'Failed to load analytics.');
        setStats({ products: 0, orders: 0, users: 0, earnings: 0 });
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getStatusKey = (status) => (status ? String(status).toLowerCase() : 'unknown');

  const aggregates = orders.reduce(
    (acc, order) => {
      const key = getStatusKey(order.status);
      if (!acc.byStatus[key]) {
        acc.byStatus[key] = { count: 0, total: 0 };
      }
      acc.byStatus[key].count += 1;
      acc.byStatus[key].total += Number(order.total || 0);
      acc.totalOrders += 1;
      acc.totalAmount += Number(order.total || 0);
      if (key === 'completed') {
        acc.completedCount += 1;
      }
      if (key === 'cancelled' || key === 'refund') {
        acc.cancelledOrRefunded += 1;
      }
      return acc;
    },
    {
      byStatus: {},
      totalOrders: 0,
      totalAmount: 0,
      completedCount: 0,
      cancelledOrRefunded: 0,
    }
  );

  const statusEntries = Object.entries(aggregates.byStatus).sort(
    (a, b) => b[1].count - a[1].count
  );

  const overallOrders = stats?.orders ?? aggregates.totalOrders;
  const completionRate =
    overallOrders > 0 ? (aggregates.completedCount / overallOrders) * 100 : 0;
  const cancellationRate =
    overallOrders > 0 ? (aggregates.cancelledOrRefunded / overallOrders) * 100 : 0;

  if (loading) {
    return (
      <div className="analytics-page">
        <AdminPageTitle title="Analytics" />
        <div className="analytics-header">
          <h1>Analytics</h1>
          <p>Loading analytics...</p>
        </div>
        <div className="analytics-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <AdminPageTitle title="Analytics" />
      <div className="analytics-header">
        <h1>Analytics</h1>
        <p>Overview of orders and performance</p>
      </div>

      {error && <div className="analytics-error">{error}</div>}

      <div className="analytics-cards">
        <div className="analytics-card">
          <h3>Total Orders</h3>
          <p className="metric">{overallOrders}</p>
        </div>
        <div className="analytics-card">
          <h3>Completed Orders</h3>
          <p className="metric">{aggregates.completedCount}</p>
        </div>
        <div className="analytics-card">
          <h3>Completion Rate</h3>
          <p className="metric">
            {completionRate.toFixed(1)}
            <span className="metric-suffix">%</span>
          </p>
        </div>
        <div className="analytics-card">
          <h3>Cancelled / Refunded</h3>
          <p className="metric">
            {aggregates.cancelledOrRefunded}{' '}
            <span className="metric-sub">
              ({cancellationRate.toFixed(1)}
              %)
            </span>
          </p>
        </div>
        <div className="analytics-card">
          <h3>Earnings (This Month)</h3>
          <p className="metric">
            ¥{Number(stats?.earnings ?? 0).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="analytics-section">
        <h2>Orders by Status</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Orders</th>
                <th>% of total</th>
                <th>Total Amount (¥)</th>
              </tr>
            </thead>
            <tbody>
              {statusEntries.length === 0 ? (
                <tr>
                  <td colSpan="4" className="no-data">
                    No orders yet
                  </td>
                </tr>
              ) : (
                statusEntries.map(([statusKey, info]) => {
                  const pct =
                    aggregates.totalOrders > 0
                      ? (info.count / aggregates.totalOrders) * 100
                      : 0;
                  const label = statusLabels[statusKey] || statusKey;
                  return (
                    <tr key={statusKey}>
                      <td>{label}</td>
                      <td>{info.count}</td>
                      <td>{pct.toFixed(1)}%</td>
                      <td>¥{info.total.toLocaleString()}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="analytics-section">
        <h2>Most Recent Orders (up to 10)</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Total (¥)</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    No orders yet
                  </td>
                </tr>
              ) : (
                orders.slice(0, 10).map((order) => (
                  <tr key={order.id}>
                    <td>{order.order_number || order.id}</td>
                    <td>{order.user?.email || order.user?.name || 'N/A'}</td>
                    <td>{statusLabels[getStatusKey(order.status)] || order.status}</td>
                    <td>¥{Number(order.total || 0).toLocaleString()}</td>
                    <td>
                      {order.created_at
                        ? new Date(order.created_at).toLocaleString()
                        : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;

